import { assertNever } from "@p0security/shared/types/index";

/** What a `data` request can ask for. `write` grants everything `read` does. */
export const DatabricksAiLevels = ["read", "write"] as const;
export type DatabricksAiLevel = (typeof DatabricksAiLevels)[number];

/**
 * What a `data` request can be granted on. Unity Catalog's permissions API
 * takes a view or materialized view as a `table`.
 */
export const DatabricksAiSecurableTypes = [
  "catalog",
  "schema",
  "table",
  "volume",
] as const;
export type DatabricksAiSecurableType =
  (typeof DatabricksAiSecurableTypes)[number];

/** What `read` grants on a catalog or schema, which its objects inherit. */
const READ_IN_CONTAINER = [
  "USE_SCHEMA",
  "SELECT",
  "READ_VOLUME",
  "EXECUTE",
] as const;

/** What `write` adds on a catalog or schema. Never `CREATE_SCHEMA`. */
const WRITE_IN_CONTAINER = [
  "MODIFY",
  "WRITE_VOLUME",
  "CREATE_TABLE",
  "CREATE_VOLUME",
  "CREATE_FUNCTION",
  "CREATE_MATERIALIZED_VIEW",
] as const;

// Each type carries the USE privilege on the securable itself: a catalog
// grant without USE_SCHEMA, which its schemas inherit, could read nothing
// under it. The USE privileges on its parents are the grant builder's, which
// adds USE_CATALOG only below a catalog and USE_SCHEMA only below a schema.
const LEVEL_PRIVILEGES = {
  catalog: {
    read: ["USE_CATALOG", ...READ_IN_CONTAINER],
    write: WRITE_IN_CONTAINER,
  },
  schema: { read: READ_IN_CONTAINER, write: WRITE_IN_CONTAINER },
  table: { read: ["SELECT"], write: ["MODIFY"] },
  volume: { read: ["READ_VOLUME"], write: ["WRITE_VOLUME"] },
} as const satisfies Record<
  DatabricksAiSecurableType,
  Record<DatabricksAiLevel, readonly string[]>
>;

/** A Unity Catalog privilege a level grants, in the permissions API's name. */
export type DatabricksAiPrivilege =
  (typeof LEVEL_PRIVILEGES)[DatabricksAiSecurableType][DatabricksAiLevel][number];

/**
 * The Unity Catalog privileges `level` grants on a securable of `type`, in the
 * names the permissions API takes (`READ_VOLUME`, not `READ VOLUME`), without
 * the USE privileges on its parents. No level grants `MANAGE`,
 * `ALL_PRIVILEGES` or `CREATE_SCHEMA`.
 */
export const privilegesFor = (
  type: DatabricksAiSecurableType,
  level: DatabricksAiLevel
): readonly DatabricksAiPrivilege[] => {
  const { read, write } = LEVEL_PRIVILEGES[type];
  return level === "read"
    ? read
    : level === "write"
      ? [...read, ...write]
      : assertNever(level);
};
